import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
} from "@mui/material";
import axios from "axios";
import React, { useContext, useEffect, useState } from "react";
import { visuallyHidden } from "@mui/utils";
import { authContext } from "../Context/Authentication/authContext";
import MSAMap from "./Components/MSAMap/MSAMap";
import "./Predictions.scss";
import { baseURL } from "../config";
import {
  getMetroGeoLocationData,
  getMetroZipcode,
  predictionTableHeaders,
  excelData,
} from "../Helpers/helpers";
import { Bars } from "react-loader-spinner";
import DownloadExcelButton from "./DownloadExcelButton";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";

const round = (num: number) => {
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

interface FilterState {
  column: string;
  operator: string;
  value: string;
}

interface HeaderCell {
  name: string;
  key: string;
  disablePadding?: boolean;
  interval?: string;
}

interface EnhancedTableProps {
  order: "asc" | "desc";
  orderBy: string;
  headers: HeaderCell[];
  interval: any;
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
  onSearchChange: (value: string) => void;
  onApplyFilter: (column: string, operator: string, value: string) => void;
  onNameFilterChange: any;
  clear: boolean;
}
const formComponentStyles = {
  select: {
    backgroundColor: "#333",
    color: "white",
    borderRadius: 1,
    height: "45px",
    "& .MuiSelect-icon": {
      color: "white",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "white !important",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "white !important",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "white !important",
    },
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      width: "120px",

      "& fieldset": {
        width: "120px",
        borderColor: "white !important",
      },
      "&:hover fieldset": {
        borderColor: "white !important",
      },
      "&.Mui-focused fieldset": {
        borderColor: "white !important",
      },
    },
    "& .MuiInputBase-input": {
      color: "white",
      height: "20px",
    },
    "& .MuiInputLabel-root": {
      color: "white",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "white",
    },
  },
  searchField: {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "white !important",
      },
      "&:hover fieldset": {
        borderColor: "white !important",
      },
      "&.Mui-focused fieldset": {
        borderColor: "white !important",
      },
    },
    "& .MuiInputBase-input": {
      color: "white",
    },
    "& .MuiInputAdornment-root .MuiSvgIcon-root": {
      color: "white",
    },
  },
};
const defaultOperator = "=";

export function EnhancedTableHead(props: EnhancedTableProps) {
  const {
    order,
    orderBy,
    onRequestSort,
    headers,
    interval,
    onSearchChange,
    onNameFilterChange,
    onApplyFilter,
    clear,
  } = props;

  // State for managing multiple filter states
  const [filterStates, setFilterStates] = useState<Record<string, FilterState>>(
    {}
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [activeColumn, setActiveColumn] = useState<string>("");
  const [nameFilter, setNameFilter] = useState<string>("");
  const [searchIconSelected, setSearchIconSelected] = useState<
    Record<string, boolean>
  >({});
  const [filterIconSelected, setFilterIconSelected] = useState<
    Record<string, boolean>
  >({});

  // Initialize filter states for each header
  useEffect(() => {
    const initialFilterStates: Record<string, FilterState> = {};
    headers.forEach((header) => {
      if (!filterStates[header.key]) {
        initialFilterStates[header.key] = {
          column: header.key,
          operator: defaultOperator,
          value: "",
        };
      }
    });
    setFilterStates((prev) => ({ ...prev, ...initialFilterStates }));
  }, [headers]);

  useEffect(() => {
    if (clear) {
      setFilterIconSelected((prevFilters) =>
        Object.keys(prevFilters).reduce((acc: any, key) => {
          acc[key] = false;
          return acc;
        }, {})
      );
      setNameFilter("");
    }
  }, [clear]);

  const createSortHandler =
    (property: string) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  const handleSearchIconClick = (
    event: React.MouseEvent<HTMLElement>,
    column: string
  ) => {
    setAnchorEl(event.currentTarget);
    setActiveColumn(column);
    setSearchIconSelected((prev) => ({ ...prev, [column]: true }));
  };

  const handleFilterIconClick = (
    event: React.MouseEvent<HTMLElement>,
    column: string
  ) => {
    setFilterAnchorEl(event.currentTarget);
    setActiveColumn(column);
    setFilterIconSelected((prev) => ({ ...prev, [column]: true }));
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchIconSelected((prev) => ({ ...prev, [activeColumn]: false }));
  };

  const handleFilterClose = () => {
    // Reset filterIconSelected for the active column if no filter was applied
    setFilterIconSelected((prev) => ({
      ...prev,
      [activeColumn]: filterStates[activeColumn]?.value ? true : false, // Retain color only if value exists
    }));
    setFilterAnchorEl(null);
  };

  const handleFilterChange = (column: string, field: string, value: string) => {
    setFilterStates((prev) => ({
      ...prev,
      [column]: {
        ...prev[column],
        [field]: value,
      },
    }));
  };
  const handleApplyFilter = (column: string) => {
    const filterState = filterStates[column];
    if (filterState.value) {
      onApplyFilter(
        filterState.column,
        filterState.operator,
        filterState.value || "0"
      );
      setFilterIconSelected((prev) => ({
        ...prev,
        [column]: true, // Change color to indicate filter is applied
      }));
    }
    handleFilterClose();
  };

  return (
    <TableHead>
      <TableRow>
        {headers.map((headCell, index) => {
          if (headCell.interval && headCell.interval !== interval) return null;
          const isSearchable = headCell.key === "metroarea";
          const currentFilter = filterStates[headCell.key];
          return (
            <TableCell
              key={headCell.key}
              padding={headCell.disablePadding ? "none" : "normal"}
              sortDirection={orderBy === headCell.key ? order : false}
              align="center"
              sx={{
                position: "sticky",
                left: 0,
                zIndex: index === 0 ? 999 : 1,
                "&:hover .search-icon, &:hover .filter-icon": {
                  color: "black",
                  display: "block",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <TableSortLabel
                  active={orderBy === headCell.key}
                  direction={orderBy === headCell.key ? order : "asc"}
                  onClick={createSortHandler(headCell.key)}
                >
                  {headCell.name}
                </TableSortLabel>

                {isSearchable ? (
                  <>
                    <IconButton
                      onClick={(e) => handleSearchIconClick(e, headCell.key)}
                      size="small"
                      sx={{ padding: 0 }}
                    >
                      <SearchIcon
                        className="search-icon"
                        sx={{
                          color: searchIconSelected[headCell.key]
                            ? "#f7b047"
                            : "white",
                          fontSize: "18px",
                        }}
                      />
                    </IconButton>
                    <IconButton
                      onClick={(e) => handleFilterIconClick(e, headCell.key)}
                      size="small"
                      sx={{ padding: 0 }}
                    >
                      <FilterListIcon
                        className="filter-icon"
                        sx={{
                          color: nameFilter != "" ? "#f7b047" : "white",
                          fontSize: "18px",
                        }}
                      />
                    </IconButton>
                    <Popover
                      open={Boolean(anchorEl) && activeColumn === headCell.key}
                      anchorEl={anchorEl}
                      onClose={handleClose}
                      anchorOrigin={{
                        vertical: "top",
                        horizontal: "center",
                      }}
                      transformOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                      }}
                    >
                      <Box p={2}>
                        <TextField
                          placeholder={`Search ${headCell.name}`}
                          size="small"
                          onChange={(e) => onSearchChange(e.target.value)}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "white",
                              },
                              "&:hover fieldset": {
                                borderColor: "white",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "white",
                              },
                            },
                            "& .MuiInputBase-input": {
                              color: "white",
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                    </Popover>
                    <Popover
                      open={
                        Boolean(filterAnchorEl) && activeColumn === headCell.key
                      }
                      anchorEl={filterAnchorEl}
                      onClose={handleFilterClose}
                      anchorOrigin={{
                        vertical: "top",
                        horizontal: "center",
                      }}
                      transformOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                      }}
                    >
                      <Box p={2}>
                        <FormControl style={{ width: "200px" }}>
                          <InputLabel>Filter by</InputLabel>
                          <Select
                            value={nameFilter}
                            sx={formComponentStyles.select}
                            onChange={(e) => setNameFilter(e.target.value)}
                          >
                            <MenuItem value="μSA">Micropoliton (μSA)</MenuItem>
                            <MenuItem value="MSA">Metropoliton (MSA)</MenuItem>
                          </Select>
                        </FormControl>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            marginTop: 1,
                          }}
                        >
                          <button
                            onClick={() => onNameFilterChange(nameFilter)}
                            style={{
                              background: "#f7b047",
                              padding: "8px",
                              borderRadius: "4px",
                              fontWeight: "bold",
                              cursor: "pointer",
                            }}
                          >
                            Apply Filter
                          </button>
                        </Box>
                      </Box>
                    </Popover>
                  </>
                ) : (
                  <>
                    <IconButton
                      onClick={(e) => handleFilterIconClick(e, headCell.key)}
                      size="small"
                      sx={{ padding: 1 }}
                    >
                      <FilterListIcon
                        className="filter-icon"
                        sx={{
                          color: filterIconSelected[headCell.key]
                            ? "#f7b047"
                            : "white",
                          fontSize: "18px",
                        }}
                      />
                    </IconButton>
                    <Popover
                      open={
                        Boolean(filterAnchorEl) && activeColumn === headCell.key
                      }
                      anchorEl={filterAnchorEl}
                      onClose={handleFilterClose}
                      anchorOrigin={{
                        vertical: "top",
                        horizontal: "center",
                      }}
                      transformOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                      }}
                    >
                      <Box p={2}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <FormControl style={{ width: "100px" }}>
                            <InputLabel>Operator</InputLabel>
                            <Select
                              value={currentFilter?.operator || defaultOperator}
                              onChange={(e) =>
                                handleFilterChange(
                                  headCell.key,
                                  "operator",
                                  e.target.value
                                )
                              }
                              label="Operator"
                              sx={formComponentStyles.select}
                            >
                              {[">", "<", ">=", "<=", "="].map((op) => (
                                <MenuItem key={op} value={op}>
                                  {op}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField
                            label="Value"
                            value={currentFilter?.value || ""}
                            onChange={(e) =>
                              handleFilterChange(
                                headCell.key,
                                "value",
                                e.target.value
                              )
                            }
                            type="number"
                            sx={formComponentStyles.textField}
                          />
                        </div>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            marginTop: 1,
                          }}
                        >
                          <button
                            onClick={() => handleApplyFilter(headCell.key)}
                            style={{
                              background: "#f7b047",
                              padding: "8px",
                              borderRadius: "4px",
                              fontWeight: "bold",
                              cursor: "pointer",
                            }}
                          >
                            Apply Filter
                          </button>
                        </Box>
                      </Box>
                    </Popover>
                  </>
                )}
              </Box>
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

const MsaPredictions = () => {
  const [interval, setInterval] = useState(false);
  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState("ppsf");

  const { state }: any = useContext(authContext);
  const [predictionsData, setPredictionsData]: any = useState([]);
  const [headers, setHeaders]: any = React.useState([]);
  const [geoJson, setGeoJson]: any = React.useState([]);
  const [isLoading, setLoading]: any = React.useState(true);
  const [xlsData, setXlsData]: any = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedFilters, setAppliedFilters] = useState<any>({});
  const [clear, setClear]: any = React.useState(false);
  const [nameFilter, setNameFilter] = useState<string>("");

  useEffect(() => {
    getMsaPredictions();
  }, [
    state.filters.predictionFor,
    state.filters.noOfPrediction,
    state.filters.selectedType,
    state.filters.state,
    state.filters.msaPrediction,
  ]);
  useEffect(() => {
    setXlsData(
      excelData(headers, filteredPredictions(state.filters.selectedType))
    );
  }, [searchQuery, appliedFilters,nameFilter]);

  const handleIntervalChange = () => {
    setInterval(!interval);
  };
  const getGeoJSONInfo = async (key: string) => {
    let resp;
    let data = {};
    resp = await fetch(`../fullgeoJsonData/${key}.json`);
    data = await resp.json();

    // if (key === 'florida') {
    // resp = await fetch('../geoJsonData/florida.json')
    // data = await resp.json()
    // } else if (key === 'california') {
    //   resp = await fetch('../geoJsonData/california.json')
    //   data = await resp.json()
    // } else if (key === 'tennessee') {
    //   resp = await fetch('../geoJsonData/tennessee.json')
    //   data = await resp.json()
    // } else if (key === 'texas') {
    //   resp = await fetch('../geoJsonData/texas.json')
    //   data = await resp.json()
    // } else if (key === 'arizona') {
    //   resp = await fetch('../geoJsonData/arizona.json')
    //   data = await resp.json()
    // }
    // else if (key === 'indiana') {
    //   resp = await fetch('../geoJsonData/indiana.json')
    //   data = await resp.json()
    // }
    // else if (key === 'north carolina') {
    //   resp = await fetch('../geoJsonData/north carolina.json')
    //   data = await resp.json()
    // }
    // else if (key === 'south carolina') {
    //   resp = await fetch('../geoJsonData/south carolina.json')
    //   data = await resp.json()
    // }
    // else if (key === 'georgia') {
    //   resp = await fetch('../geoJsonData/georgia.json')
    //   data = await resp.json()
    // }
    // else if (key === 'illinois') {
    //   resp = await fetch('../geoJsonData/illinois.json')
    //   data = await resp.json()
    // }
    // else if (key === 'idaho') {
    //   resp = await fetch('../geoJsonData/idaho.json')
    //   data = await resp.json()
    // }
    // else if (key === 'minnesota') {
    //   resp = await fetch('../geoJsonData/minnesota.json')
    //   data = await resp.json()
    // }
    // else if (key === 'utah') {
    //   resp = await fetch('../geoJsonData/utah.json')
    //   data = await resp.json()
    // }
    // else if (key === 'colorado') {
    //   resp = await fetch('../geoJsonData/colorado.json')
    //   data = await resp.json()
    // }
    // else if (key === 'washington') {
    //   resp = await fetch('../geoJsonData/washington.json')
    //   data = await resp.json()
    // }
    // else if (key === 'oregon') {
    //   resp = await fetch('../geoJsonData/oregon.json')
    //   data = await resp.json()
    // }
    // else if (key === 'virginia') {
    //   resp = await fetch('../geoJsonData/virginia.json')
    //   data = await resp.json()
    // }
    // else if (key === 'wisconsin') {
    //   resp = await fetch('../geoJsonData/wisconsin.json')
    //   data = await resp.json()
    // }
    // else if (key === 'alabama') {
    //   resp = await fetch('../geoJsonData/alabama.json')
    //   data = await resp.json()
    // }
    // else if (key === 'alabama') {
    //   resp = await fetch('../geoJsonData/alabama.json')
    //   data = await resp.json()
    // }

    return data;
  };
  const getIndex = (nestedArray: any, zip: any) => {
    for (let outerIndex = 0; outerIndex < nestedArray.length; outerIndex++) {
      const subArray = nestedArray[outerIndex];

      // Flatten the subArray and search for the target zip code
      const flattenedSubArray = subArray.flat(Infinity);
      if (flattenedSubArray.includes(zip)) {
        return outerIndex; // Return the outer index if found
      }
    }

    return -1; // Return -1 if not found
  };
  function findZipCodeData(nestedArray: any, zipData: any, availableMsa: any) {
    const result: any = [];

    // Helper function to recursively loop through the nested array
    function searchZipCodes(arr: any) {
      arr.forEach((item: any, index: any) => {
        if (Array.isArray(item)) {
          searchZipCodes(item); // Recursively search through arrays
        } else {
          if (zipData[item]) {
            const index = getIndex(nestedArray, item);

            zipData[item].properties.metroArea = availableMsa[index];
            result.push(zipData[item]);
          }
        }
      });
    }

    searchZipCodes(nestedArray);
    return result;
  }

  const getMsaPredictions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${baseURL}getMetroPredictionSummary?state=${state?.filters?.state}&date=${state.filters.predictionFor}&limit=${state.filters.noOfPrediction}`
      );
      const tableHeaders = predictionTableHeaders(state.filters.selectedType);
      setHeaders(tableHeaders);
      setPredictionsData(response.data?.cube);
      setXlsData(excelData(tableHeaders, response.data?.cube));
      const availableMsa: string[] = [];
      const promises: any[] = [];
      response.data?.cube.forEach((m: any) => {
        availableMsa.push(
          `${m.metroarea} ${m.type === "Micro" ? "MSA" : "μSA"}`
        );
        promises.push(getMetroZipcode(state?.filters.state, m.metroarea));
      });
      const result = await Promise.all(promises);
      const geoInfo: any[] = [];
      const geoPromises: any = [];
      const foundZipCodes = findZipCodeData(
        result,
        await getGeoJSONInfo(state.filters.state.toLowerCase()),
        availableMsa
      );
      const MsaInformation = await Promise.all(geoPromises);
      MsaInformation.forEach((msaInfo: any, index: any) => {
        msaInfo.forEach((info: any) => {
          const i = { ...info };
          i.properties.metroArea = availableMsa[index];
          geoInfo.push(i);
        });
      });

      setGeoJson(foundZipCodes);
    } catch (error) {
      console.log("error", error);
    }
    setLoading(false);
  };

  function stableSort<T>(
    array: readonly T[],
    comparator: (a: T, b: T) => number
  ) {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: any
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };
  const handleApplyFilter = (
    column: string,
    operator: string,
    value: string
  ) => {
    setClear(false);
    setAppliedFilters((prevFilters: any) => {
      // Check if the column already exists in the state
      if (prevFilters[column]) {
        return {
          ...prevFilters,
          [column]: prevFilters[column].map((filter: any, index: number) => {
            if (index === 0) {
              // Update the first entry for this column
              return { operator, value };
            }
            return filter;
          }),
        };
      } else {
        // If the column does not exist, add a new entry
        return {
          ...prevFilters,
          [column]: [{ operator, value }],
        };
      }
    });
  };

  const filteredPredictions = (type: string) => {
    let filteredData = predictionsData;
    Object.entries(appliedFilters).forEach(([column, columnFilters]: any) => {
      columnFilters.forEach((filter: any) => {
        filteredData = filteredData.filter((row: any) => {
          const cellValue = round(row[column]);
          const filterValue = parseFloat(filter.value);
          const operator = filter.operator;
          switch (operator) {
            case ">":
              return cellValue > filterValue;
            case "<":
              return cellValue < filterValue;
            case ">=":
              return cellValue >= filterValue;
            case "<=":
              return cellValue <= filterValue;
            case "=":
              return cellValue === filterValue;
            default:
              return true;
          }
        });
      });
    });
    if (nameFilter.trim() !== "") {
      filteredData = filteredData.filter((row: any) =>
        nameFilter === "μSA" ? row.type === "Micro" : row.type !== "Micro"
      );
    }

    // Apply searchQuery on `metroarea`
    if (searchQuery.trim() !== "") {
      const query = searchQuery.toLowerCase();
      filteredData = filteredData.filter((row: any) =>
        row.metroarea?.toLowerCase().includes(query)
      );
    }
    return filteredData;
  };

  const handleNameFilterChnage = (val: string) => {
    setNameFilter(val);
  };
  const handleClearFilters = () => {
    setClear(true);
    setSearchQuery("");
    setAppliedFilters({});
    getMsaPredictions();
    setNameFilter("");
  };

  return (
    <div className="predictions-main">
      <div className="predictions-main__map-container">
        {isLoading ? (
          <Bars
            height="400"
            ariaLabel="bars-loading"
            wrapperClass="msa-spinner-progress-bar"
            visible={true}
          />
        ) : (
          <MSAMap geoData={geoJson} />
        )}
      </div>
      <div className="predictions-main__interval-check">
        <FormControlLabel
          label="Click here for interval estimates"
          control={
            <Checkbox checked={interval} onChange={handleIntervalChange} />
          }
        />

        <DownloadExcelButton
          jsonData={xlsData}
          type={"msa-prediction-summary"}
        />
        {(Object.keys(appliedFilters).length != 0 ||
          nameFilter != "" ||
          searchQuery.trim() != "") && (
          <Button
            className="excel-button"
            style={{ marginRight: "12px" }}
            // endIcon={<DownloadIcon/>}
            onClick={handleClearFilters}
            // disabled={type === ''}
          >
            Clear Filters
          </Button>
        )}
      </div>
      <div className="predictions-main__table-container">
        <TableContainer component={Paper} sx={{ maxHeight: 330 }}>
          <Table stickyHeader aria-label="simple table">
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              headers={headers}
              interval={interval}
              onSearchChange={handleSearchChange}
              onApplyFilter={handleApplyFilter}
              onNameFilterChange={handleNameFilterChnage}
              clear={clear}
            />
            <TableBody>
              {stableSort(
                filteredPredictions(state?.filters?.selectedType),
                getComparator(order, orderBy)
              ).map((row: any, index: number) => {
                const labelId = `enhanced-table-checkbox-${index}`;
                return (
                  <TableRow
                    key={index}
                    className="predictions-main__table-container__row"
                  >
                    <TableCell
                      className="predictions-main__table-container__row__cell"
                      id={labelId}
                      component="th"
                      scope="row"
                      align="left"
                    >
                      {row.metroarea} {row.type != "Micro" ? "MSA" : "μSA"}
                    </TableCell>
                    {interval && (
                      <TableCell align="center">{round(row.ppsfLow)}</TableCell>
                    )}
                    <TableCell align="center">{round(row.ppsf)}</TableCell>
                    {interval && (
                      <TableCell align="center">
                        {round(row.ppsfHigh)}
                      </TableCell>
                    )}
                    {interval && (
                      <TableCell align="center">
                        {round(row.ppsfLowGrowth)}
                      </TableCell>
                    )}
                    <TableCell align="center">
                      {round(row.ppsfMeanGrowth)}
                    </TableCell>
                    {interval && (
                      <TableCell align="center">
                        {round(row.ppsfHighGrowth)}
                      </TableCell>
                    )}
                    {interval && (
                      <TableCell align="center">{round(row.mspLow)}</TableCell>
                    )}
                    <TableCell align="center">{round(row.msp)}</TableCell>
                    {interval && (
                      <TableCell align="center">{round(row.mspHigh)}</TableCell>
                    )}
                    {interval && (
                      <TableCell align="center">
                        {round(row.mspLowGrowth)}
                      </TableCell>
                    )}
                    <TableCell align="center">
                      {round(row.mspMeanGrowth)}
                    </TableCell>
                    {interval && (
                      <TableCell align="center">
                        {round(row.mspHighGrowth)}
                      </TableCell>
                    )}
                    {interval && (
                      <TableCell align="center">{round(row.domLow)}</TableCell>
                    )}
                    <TableCell align="center">{round(row.domMean)}</TableCell>
                    {interval && (
                      <TableCell align="center">{round(row.domHigh)}</TableCell>
                    )}
                    {interval && (
                      <TableCell align="center">
                        {round(row.domLowGrowth)}
                      </TableCell>
                    )}
                    <TableCell align="center">
                      {round(row.mspMeanGrowth)}
                    </TableCell>
                    {interval && (
                      <TableCell align="center">
                        {round(row.mspHighGrowth)}
                      </TableCell>
                    )}
                    {interval && (
                      <TableCell align="center">
                        {round(row.homeSalesLow)}
                      </TableCell>
                    )}
                    <TableCell align="center">
                      {round(row.homeSalesMean)}
                    </TableCell>
                    {interval && (
                      <TableCell align="center">
                        {round(row.homeSalesHigh)}
                      </TableCell>
                    )}
                    {interval && (
                      <TableCell align="center">
                        {row.home_sales_cumulative_low
                          ? round(row.home_sales_cumulative_low)
                          : "-"}
                      </TableCell>
                    )}
                    <TableCell align="center">
                      {row.home_sales_cumulative_mean
                        ? round(row.home_sales_cumulative_mean)
                        : "-"}
                    </TableCell>
                    {interval && (
                      <TableCell align="center">
                        {row.home_sales_cumulative_high
                          ? round(row.home_sales_cumulative_high)
                          : "-"}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {predictionsData?.length === 0 && (
            <div className="predictions-main__noData">No data available</div>
          )}
        </TableContainer>
      </div>
      <div className="predictions-main__notes-container">
        <div className="predictions-main__notes-container__heading">Notes</div>
        <ul>
          <li>DOM : Days on Market</li>
          <li>HS : Home Sales</li>
          <li>HSC : Home Sales Cumulative</li>
          <li>MSA : Micropolitan Statistical Area</li>
          <li>μSA : Micropolitan Statistical Area</li>
        </ul>
      </div>
    </div>
  );
};

export default MsaPredictions;
